import { chain } from '@leprecoins/effector';
import dayjs from 'dayjs';
import { attach } from 'effector';
import { ShiftReportRegistryModel } from 'entities/shift';
import {
	$t,
	createExportReportModel,
	EXPORT_FORMAT,
	ExportFormat,
	ExportReportModel,
} from 'shared/lib';

export type ExportReportShiftsModel = ExportReportModel;

export const createExportReportShiftsModel = (
	model: ShiftReportRegistryModel,
): ExportReportShiftsModel => {
	const exportReportModel = createExportReportModel();

	const exportFx = attach({
		source: [
			model.registryModel.$rows,
			model.$total,
			model.$isMultiCurrencies,
			$t,
			model.dateFilterModel.dateFilter.$value,
		] as const,
		effect: ([rows, total, isMultiCurrencies, t, dateFilter], format: ExportFormat) => {
			const headers = [
				t('shift-report-registry.table-columns.cashier'),
				t('shift-report-registry.table-columns.cashdesk'),
				t('shift-report-registry.table-columns.account'),
				t('shift-report-registry.table-columns.start-time'),
				t('shift-report-registry.table-columns.end-time'),
				t('shift-report-registry.table-columns.currency'),
				t('shift-report-registry.table-columns.balance-open'),
				t('shift-report-registry.table-columns.balance-close'),
				t('shift-report-registry.table-columns.deposits'),
				t('shift-report-registry.table-columns.withdrawals'),
				t('shift-report-registry.table-columns.jackpots'),
				t('shift-report-registry.table-columns.bonuses'),
				t('shift-report-registry.table-columns.income'),
				t('shift-report-registry.table-columns.income-all-sources-tooltip'),
				t('shift-report-registry.table-columns.rtp'),
				t('shift-report-registry.table-columns.rtp-all-sources-tooltip'),
				t('shift-report-registry.table-columns.number-of-bets'),
			];

			const body = rows.map((row) => [
				row.cashier.name,
				row.cashdesk.name,
				row.account.name,
				dayjs(row.startTime).format('YYYY-MM-DD HH:mm:ss'),
				row.endTime ? dayjs(row.endTime).format('YYYY-MM-DD HH:mm:ss') : '',
				row.currency,
				row.openingBalance.toFixed(2),
				row.closingBalance.toFixed(2),
				row.deposits.toFixed(2),
				row.withdrawals.toFixed(2),
				row.jackpots.toFixed(2),
				row.bonuses.toFixed(2),
				row.income.toFixed(2),
				row.incomeAllSources.toFixed(2),
				`${(row.rtp * 100).toFixed(2)}`,
				`${(row.rtpAllSources * 100).toFixed(2)}`,
				row.numberOfBets,
			]);

			const footer = [
				t('Total'),
				'',
				'',
				'',
				'',
				total?.currency || '',
				'',
				'',
				isMultiCurrencies ? '' : total?.deposits.toFixed(2) || 0,
				isMultiCurrencies ? '' : total?.withdrawals.toFixed(2) || 0,
				isMultiCurrencies ? '' : total?.jackpots.toFixed(2) || 0,
				isMultiCurrencies ? '' : total?.bonuses.toFixed(2) || 0,
				isMultiCurrencies ? '' : total?.income.toFixed(2) || 0,
				isMultiCurrencies ? '' : total?.incomeAllSources.toFixed(2) || 0,
				`${total?.rtp ?? 0} %`,
				`${(total?.rtpAllSources ?? 0) * 100} %`,
				total?.numberOfBets || 0,
			];

			if (format === EXPORT_FORMAT.xlsx) {
				exportReportModel.saveAsExcelFx({
					data: [headers, ...body, footer],
					dates: dateFilter,
					filenameTemplate: 'shift-report%DATE%.xlsx',
					sheetTitle: 'Shift Report',
					columnWidths: [
						undefined,
						undefined,
						undefined,
						undefined,
						undefined,
						undefined,
						undefined,
						25,
						undefined,
						25,
						undefined,
					],
					enableBorders: true,
				});

				return;
			}

			exportReportModel.saveAsPdfFx({
				head: [headers],
				body: [...body],
				footer: [footer],
				dates: dateFilter,
				filenameTemplate: 'shift-report%DATE%.pdf',
				tableOptions: {
					styles: {
						fontSize: 8,
					},
				},
				documentOptions: {
					orientation: 'landscape',
				},
			});
		},
	});

	chain(exportReportModel.exportButtonClicked, exportFx);

	return exportReportModel;
};
